import isEqual from 'react-fast-compare';
import {
  FilterToolbarGroup,
  FilterToolbarItem,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { StandalonePurchaseVendorAccountsSelector } from 'src/components/Selectors/PurchaseVendorAccountSelector';
import { useContent } from 'src/contexts/ContentContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COLLECTION_FILTER_OP_TO_CID,
  YES_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { PurchaseDateRangePresetNames } from 'src/utils/dateTimeUtils';
import { FromYesNoEnum, ToYesNoEnum } from 'src/utils/eventQueryUtils';
import { ReportTypes } from 'src/utils/reportsUtils';
import {
  DateTimeRangeWithRelative,
  Feature,
  FilterOperationType,
} from 'src/WebApiController';

import { useDefaultYesNoFilterValues } from '../../Filters/hooks/useDefaultYesNoFilterValues';
import { usePurchaseVendorAccountSelector } from '../../Selectors/PurchaseVendorAccountSelector/usePurchaseVendorAccountSelector';
import { PurchaseVendorMultiSelector } from '../../Selectors/PurchaseVendorSelector';
import { SellerAccountEmployeeMultiSelector } from '../../Selectors/SellerAccountEmployeeSelector';
import { VendorAccessControlProvidersSelector } from '../../Selectors/VendorAccessControlProvidersSelector/VendorAccessControlProvidersSelector';

export type PurchaseInfoFilter = {
  purchaseVendorIds?: number[] | null;
  purchaseVendorAccountIds?: string[] | null;
  purchaseBuyerIds?: string[] | null;
  purchaseOrderCreatedBys?: string[] | null;
  purchaseDates?: DateTimeRangeWithRelative | null;
  purchaseCommissionUserIds?: string[] | null;
  purchaseCancellationDates?: DateTimeRangeWithRelative | null;
  purchaseCancelledOnly?: boolean | null;
  vendorAccessControlProviderId?: number | null;
  purchaseVendorOrderId?: string | null;

  operations?: {
    purchaseVendorIdOp?: FilterOperationType | null;
    purchaseVendorAccountIdsOp?: FilterOperationType | null;
    purchaseBuyerIdOp?: FilterOperationType | null;
    purchaseOrderCreatedByOp?: FilterOperationType | null;
    purchaseDatesOp?: FilterOperationType | null;
    purchaseCommissionUserIdOp?: FilterOperationType | null;
    purchaseCancellationDatesOp?: FilterOperationType | null;
  } | null;
};

export const usePurchaseInfoFilters = <TFilter extends PurchaseInfoFilter>({
  query,
  setQuery,
  reportType,
  showCommissionerFilter,
  showCancellationDateFilter,
  useRelativePresets,
  supportMultiOperation = false,
  isHorizontalLayout = false,
}: {
  query: TFilter;
  setQuery: (query: TFilter) => void;
  reportType?: ReportTypes;
  showCommissionerFilter?: boolean;
  showCancellationDateFilter?: boolean;
  useRelativePresets?: boolean;
  supportMultiOperation?: boolean;
  isHorizontalLayout?: boolean;
}): FilterToolbarGroup => {
  const isReportPage = reportType != null;

  const hasPurchaseLineItemReportFeature = useUserHasFeature(
    Feature.PurchaseLineItemReport
  );

  const { query: vendorAccountQuery } = usePurchaseVendorAccountSelector({
    vendorId: query.purchaseVendorIds,
  });

  const metricsIconNote = useContent(ContentId.ThisFilterWillChangeMetrics);
  const { shouldEnableEmptySelection } = useDefaultYesNoFilterValues({
    isReportsPage: reportType != null,
  });

  return {
    titleContentId: ContentId.Purchase,
    type: 'row',
    items: [
      {
        filterId: 'purchaseVendorIds' as const,
        labelContentId: ContentId.Vendor,
        filterQueryKeys: ['purchaseVendorIds'] as FilterToolbarItemId[],
        filterItem: (
          <PurchaseVendorMultiSelector
            style={{ width: '100%' }}
            placeholderText={ContentId.AllVendors}
            enableEmptySelection
            values={query.purchaseVendorIds?.map(String) ?? []}
            operation={query.operations?.purchaseVendorIdOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            onChange={({ newValues, operation }) => {
              let newPurchaseVendorIds: number[] | null = newValues
                .filter((value) => !isNaN(Number(value)))
                .map(Number);

              if (!newPurchaseVendorIds?.length) {
                newPurchaseVendorIds = null;
              }

              if (
                !isEqual(query.purchaseVendorIds, newPurchaseVendorIds) ||
                operation !== query.operations?.purchaseVendorIdOp
              ) {
                setQuery({
                  ...query,
                  purchaseVendorIds: newPurchaseVendorIds,
                  purchaseVendorAccountIds: null, // changing vendor should reset the vendor-account selection filter
                  operations: supportMultiOperation
                    ? {
                        ...query.operations,
                        purchaseVendorIdOp: operation,
                      }
                    : undefined,
                });
                vendorAccountQuery.refetch();
              }
            }}
          />
        ),
      },
      {
        filterId: 'purchaseVendorAccountIds' as const,
        labelContentId: ContentId.VendorAccount,
        filterQueryKeys: ['purchaseVendorAccountIds'] as FilterToolbarItemId[],
        filterItem: (
          <StandalonePurchaseVendorAccountsSelector
            query={query}
            setQuery={setQuery}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
          />
        ),
      },
      !reportType
        ? {
            filterId: 'vendorAccessControlProviderId' as const,
            labelContentId: ContentId.VendorGroup,
            filterQueryKeys: [
              'vendorAccessControlProviderId',
            ] as FilterToolbarItemId[],
            filterItem: (
              <VendorAccessControlProvidersSelector
                style={{ width: '100%' }}
                placeholderText={ContentId.VendorGroup}
                value={String(query.vendorAccessControlProviderId ?? '')}
                onChange={(vendorAccessControlProviderId: string | null) => {
                  if (vendorAccessControlProviderId) {
                    setQuery({
                      ...query,
                      vendorAccessControlProviderId: Number(
                        vendorAccessControlProviderId
                      ),
                    });
                  }
                }}
              />
            ),
          }
        : null,
      {
        filterId: 'purchaseBuyerIds' as const,
        labelContentId: ContentId.PurchasedBy,
        filterQueryKeys: ['purchaseBuyerIds'] as FilterToolbarItemId[],
        filterItem: (
          <SellerAccountEmployeeMultiSelector
            values={query.purchaseBuyerIds ?? []}
            operation={query.operations?.purchaseBuyerIdOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            enableEmptySelection
            style={{ width: '100%' }}
            onChange={({ newValues, operation }) => {
              let newPurchaseBuyerIds: string[] | null = newValues
                .filter((value) => value !== EMPTY_GUID)
                .map((value) => value.trim());

              if (!newPurchaseBuyerIds?.length) {
                newPurchaseBuyerIds = null;
              }

              if (
                !isEqual(query.purchaseBuyerIds, newPurchaseBuyerIds) ||
                operation !== query.operations?.purchaseBuyerIdOp
              ) {
                setQuery({
                  ...query,
                  purchaseBuyerIds: newPurchaseBuyerIds,
                  operations: supportMultiOperation
                    ? {
                        ...query.operations,
                        purchaseBuyerIdOp: operation,
                      }
                    : undefined,
                });
              }
            }}
          />
        ),
      },
      {
        filterId: 'purchaseDates' as const,
        labelContentId: ContentId.PurchaseDate,
        filterQueryKeys: ['purchaseDates'] as FilterToolbarItemId[],
        filterItem: (
          <DateRangeSelector
            presetNames={PurchaseDateRangePresetNames}
            value={query.purchaseDates}
            onBlur={({ value }) =>
              setQuery({
                ...query,
                purchaseDates: value,
              })
            }
            useRelativePresets={useRelativePresets}
            operation={query.operations?.purchaseDatesOp ?? null}
            enableOperationSelect={supportMultiOperation}
            isHorizontalLayout={isHorizontalLayout}
          />
        ),
      },
      {
        filterId: 'purchaseOrderCreatedBys' as const,
        labelContentId: ContentId.CreatedBy,
        filterQueryKeys: ['purchaseOrderCreatedBys'] as FilterToolbarItemId[],
        filterItem: (
          <SellerAccountEmployeeMultiSelector
            values={query.purchaseOrderCreatedBys ?? []}
            operation={query.operations?.purchaseOrderCreatedByOp ?? null}
            operationOptionsContent={
              supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
            }
            isHorizontalLayout={isHorizontalLayout}
            enableEmptySelection
            style={{ width: '100%' }}
            onChange={({ newValues, operation }) => {
              let newPurchaseOrderCreatedBys: string[] | null = newValues
                .filter((value) => value !== EMPTY_GUID)
                .map((value) => value.trim());

              if (!newPurchaseOrderCreatedBys?.length) {
                newPurchaseOrderCreatedBys = null;
              }

              if (
                !isEqual(
                  query.purchaseOrderCreatedBys,
                  newPurchaseOrderCreatedBys
                ) ||
                operation !== query.operations?.purchaseOrderCreatedByOp
              ) {
                setQuery({
                  ...query,
                  purchaseOrderCreatedBys: newPurchaseOrderCreatedBys,
                  operations: supportMultiOperation
                    ? {
                        ...query.operations,
                        purchaseOrderCreatedByOp: operation,
                      }
                    : undefined,
                });
              }
            }}
          />
        ),
      },
      ...(isReportPage || showCommissionerFilter
        ? [
            {
              filterId: 'purchaseCommissionUserIds' as FilterToolbarItemId,
              labelContentId: ContentId.Commissioner,
              filterQueryKeys: [
                'purchaseCommissionUserIds',
              ] as FilterToolbarItemId[],
              filterItem: (
                <SellerAccountEmployeeMultiSelector
                  values={query.purchaseCommissionUserIds ?? []}
                  operation={
                    query.operations?.purchaseCommissionUserIdOp ?? null
                  }
                  operationOptionsContent={
                    supportMultiOperation
                      ? COLLECTION_FILTER_OP_TO_CID
                      : undefined
                  }
                  isHorizontalLayout={isHorizontalLayout}
                  enableEmptySelection
                  style={{ width: '100%' }}
                  onChange={({ newValues, operation }) => {
                    let newPurchaseCommissionUserIds: string[] | null =
                      newValues
                        .filter((value) => value !== EMPTY_GUID)
                        .map((value) => value.trim());

                    if (!newPurchaseCommissionUserIds?.length) {
                      newPurchaseCommissionUserIds = null;
                    }

                    if (
                      !isEqual(
                        query.purchaseCommissionUserIds,
                        newPurchaseCommissionUserIds
                      ) ||
                      operation !== query.operations?.purchaseCommissionUserIdOp
                    ) {
                      setQuery({
                        ...query,
                        purchaseCommissionUserIds: newPurchaseCommissionUserIds,
                        operations: supportMultiOperation
                          ? {
                              ...query.operations,
                              purchaseCommissionUserIdOp: operation,
                            }
                          : undefined,
                      });
                    }
                  }}
                />
              ),
            },
          ]
        : []),
      ...(hasPurchaseLineItemReportFeature
        ? [
            {
              filterId: 'purchaseVendorOrderId' as FilterToolbarItemId,
              labelContentId: ContentId.ExternalPurchaseId,
              filterQueryKeys: [
                'purchaseVendorOrderId',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosTextField
                  style={{ width: '100%' }}
                  value={query.purchaseVendorOrderId ?? ''}
                  type="text"
                  onChange={(e) => {
                    const newValue = e.target.value?.length
                      ? e.target.value.trim()
                      : null;
                    if (query.purchaseVendorOrderId !== newValue) {
                      setQuery({
                        ...query,
                        purchaseVendorOrderId: newValue,
                      });
                    }
                  }}
                  maxLength={200}
                />
              ),
            },
          ]
        : []),
      ...(reportType === ReportTypes.Inventory || showCancellationDateFilter
        ? [
            {
              filterId: 'purchaseCancellationDates' as FilterToolbarItemId,
              labelContentId: ContentId.CancellationDate,
              filterQueryKeys: [
                'purchaseCancellationDates',
              ] as FilterToolbarItemId[],
              filterItem: (
                <DateRangeSelector
                  presetNames={PurchaseDateRangePresetNames}
                  value={query.purchaseCancellationDates}
                  onBlur={({ value }) =>
                    setQuery({
                      ...query,
                      purchaseCancellationDates: value,
                    })
                  }
                  useRelativePresets={useRelativePresets}
                  operation={
                    query.operations?.purchaseCancellationDatesOp ?? null
                  }
                  enableOperationSelect={supportMultiOperation}
                  isHorizontalLayout={isHorizontalLayout}
                />
              ),
            },
            {
              filterId: 'purchaseCancelledOnly' as FilterToolbarItemId,
              labelContentId: ContentId.Cancelled,
              filterQueryKeys: [
                'purchaseCancelledOnly',
              ] as FilterToolbarItemId[],
              filterItem: (
                <PosEnumSelect
                  style={{ width: '100%' }}
                  value={ToYesNoEnum(query.purchaseCancelledOnly)}
                  placeholderText={ContentId.No}
                  enableEmptySelection={shouldEnableEmptySelection(
                    'purchaseCancelledOnly' as FilterToolbarItemId
                  )}
                  onChange={(yesNoEnumValue) => {
                    if (
                      yesNoEnumValue !==
                      ToYesNoEnum(query.purchaseCancelledOnly)
                    ) {
                      setQuery({
                        ...query,
                        purchaseCancelledOnly: FromYesNoEnum(yesNoEnumValue),
                      });
                    }
                  }}
                  valueOptionsContent={YES_ENUM_FILTER_TO_CID}
                />
              ),
            },
          ]
        : []),
    ].filter((f) => f!) as FilterToolbarItem[],
  };
};
